import { Injectable, inject } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, catchError, map, throwError } from 'rxjs';
import { toAppError } from '@core/errors/app-error';
import { SKIP_SESSION_EXPIRED_CONTEXT } from '@core/graphql/graphql-context';
import { toAdminProfile, toTokenPair } from './admin-auth.mapper';
import { AdminProfile } from './admin-profile';
import { ADMIN_ME, LOGOUT_ADMIN, REFRESH_ADMIN_TOKEN } from './graphql/admin-auth.documents';
import {
  AdminMeResponse,
  LogoutAdminResponse,
  LogoutAdminVariables,
  RefreshAdminTokenResponse,
  RefreshAdminTokenVariables,
} from './graphql/admin-auth.dto';
import { TokenPair } from './token-pair';

/**
 * Session operations shared by the whole app (refresh, logout, current admin). Every call uses
 * `no-cache`: tokens and the admin profile never land in the Apollo cache. Errors are `AppError`s.
 */
@Injectable({ providedIn: 'root' })
export class AdminAuthApi {
  private readonly apollo = inject(Apollo);

  /** Rotates the pair. Handles its own errors: never routed through the global session-expired link. */
  refresh(refreshToken: string): Observable<TokenPair> {
    return this.apollo
      .mutate<RefreshAdminTokenResponse, RefreshAdminTokenVariables>({
        mutation: REFRESH_ADMIN_TOKEN,
        variables: { input: { refreshToken } },
        fetchPolicy: 'no-cache',
        context: SKIP_SESSION_EXPIRED_CONTEXT,
      })
      .pipe(
        map(({ data }) => toTokenPair(data?.refreshAdminToken)),
        catchError((error: unknown) => throwError(() => toAppError(error))),
      );
  }

  /** Ends the current server session (best effort; the caller clears the local session anyway). */
  logout(refreshToken: string | null): Observable<boolean> {
    return this.apollo
      .mutate<LogoutAdminResponse, LogoutAdminVariables>({
        mutation: LOGOUT_ADMIN,
        variables: { input: refreshToken ? { refreshToken } : {} },
        fetchPolicy: 'no-cache',
        context: SKIP_SESSION_EXPIRED_CONTEXT,
      })
      .pipe(
        map(({ data }) => data?.logoutAdmin === true),
        catchError((error: unknown) => throwError(() => toAppError(error))),
      );
  }

  me(): Observable<AdminProfile> {
    return this.apollo.query<AdminMeResponse>({ query: ADMIN_ME, fetchPolicy: 'no-cache' }).pipe(
      map(({ data }) => toAdminProfile(data?.adminMe)),
      catchError((error: unknown) => throwError(() => toAppError(error))),
    );
  }
}
